import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { KeyRound, X } from 'lucide-react';
import './content-central-sheet.css';

/**
 * Asks for a Content Central integration key in Paige's own small window, over New job: CapturePoint asks for it
 * in a protected system window; in Paige the key goes from this field straight to the API, which checks it and
 * keeps it sealed, and the page keeps nothing of it. Resolves with the key, or null when the person cancels.
 */
export function askContentCentralKey(connectionName: string): Promise<string | null> {
  const host = document.createElement('div');
  document.body.append(host);
  const root = createRoot(host);
  return new Promise((resolve) => {
    const done = (key: string | null) => {
      root.unmount();
      host.remove();
      resolve(key);
    };
    root.render(<KeyDialog name={connectionName} onDone={done} />);
  });
}

function KeyDialog({ name, onDone }: { name: string; onDone: (key: string | null) => void }) {
  const [key, setKey] = useState('');
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);
  return (
    <div className="paige-intake-backdrop paige-cc-key-backdrop" onClick={() => onDone(null)}>
      <form
        className="paige-intake-sheet paige-destination-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paige-cc-key-title"
        onClick={(event) => event.stopPropagation()}
        // New job's own keys (Escape closes it, Tab stays in it) are not this window's.
        onKeyDown={(event) => (event.stopPropagation(), event.key === 'Escape' && onDone(null))}
        onSubmit={(event) => (event.preventDefault(), key.trim() && onDone(key.trim()))}
      >
        <KeyHead name={name} onClose={() => onDone(null)} />
        <label>
          Integration key
          <input
            ref={field}
            type="password"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder="ccapi_..."
            autoComplete="new-password"
            required
          />
        </label>
        <KeyFooter ready={Boolean(key.trim())} onCancel={() => onDone(null)} />
      </form>
    </div>
  );
}

function KeyHead({ name, onClose }: { name: string; onClose: () => void }) {
  return (
    <>
      <button type="button" className="paige-intake-close" onClick={onClose} aria-label="Close">
        <X size={16} />
      </button>
      <header className="paige-intake-head">
        <KeyRound size={26} aria-hidden="true" />
        <h2 id="paige-cc-key-title">Enter the key for {name}</h2>
        <p>
          In Content Central: Administration › API Connections › Paige › create a connection, then
          copy its key here. Paige checks it with Content Central and keeps it sealed.
        </p>
      </header>
    </>
  );
}

function KeyFooter({ ready, onCancel }: { ready: boolean; onCancel: () => void }) {
  return (
    <footer className="paige-destination-actions-row">
      <span className="spacer" />
      <button type="button" className="secondary-button" onClick={onCancel}>
        Cancel
      </button>
      <button type="submit" className="primary-button" disabled={!ready}>
        Connect
      </button>
    </footer>
  );
}
